import * as THREE from 'three'
import { unionMeshes } from '@/graphics/csg-operations'
import { createMeshFromInequalities } from '@/graphics/geometry'
import type { BnBNode } from '@/game/bnb-manager'

const cloneMaterial = (material: THREE.Material | THREE.Material[] | undefined): THREE.Material | THREE.Material[] | undefined => {
  if (!material) return undefined
  if (Array.isArray(material)) {
    return material.map((item) => item.clone())
  }
  return material.clone()
}

const disposeMaterial = (material: THREE.Material | THREE.Material[] | undefined): void => {
  if (!material) return
  if (Array.isArray(material)) {
    material.forEach((item) => item.dispose())
    return
  }
  material.dispose()
}

const disposeObject3DDeep = (obj: THREE.Object3D): void => {
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
      child.geometry?.dispose?.()
      disposeMaterial(child.material as THREE.Material | THREE.Material[] | undefined)
    }
  })
}

const cloneGroupDeep = (group: THREE.Group): THREE.Group => {
  const cloneObject = (obj: THREE.Object3D): THREE.Object3D => {
    if (obj instanceof THREE.Mesh) {
      const next = new THREE.Mesh(obj.geometry.clone(), cloneMaterial(obj.material as THREE.Material | THREE.Material[] | undefined))
      next.name = obj.name
      next.userData = { ...obj.userData }
      next.position.copy(obj.position)
      next.quaternion.copy(obj.quaternion)
      next.scale.copy(obj.scale)
      next.renderOrder = obj.renderOrder
      next.visible = obj.visible
      next.castShadow = obj.castShadow
      next.receiveShadow = obj.receiveShadow
      next.frustumCulled = obj.frustumCulled
      obj.children.forEach((child) => next.add(cloneObject(child)))
      return next
    }

    if (obj instanceof THREE.LineSegments) {
      const next = new THREE.LineSegments(
        obj.geometry.clone(),
        cloneMaterial(obj.material as THREE.Material | THREE.Material[] | undefined) as THREE.LineBasicMaterial,
      )
      next.name = obj.name
      next.userData = { ...obj.userData }
      next.position.copy(obj.position)
      next.quaternion.copy(obj.quaternion)
      next.scale.copy(obj.scale)
      next.renderOrder = obj.renderOrder
      next.visible = obj.visible
      next.frustumCulled = obj.frustumCulled
      obj.children.forEach((child) => next.add(cloneObject(child)))
      return next
    }

    const next = new THREE.Group()
    next.name = obj.name
    next.userData = { ...obj.userData }
    next.position.copy(obj.position)
    next.quaternion.copy(obj.quaternion)
    next.scale.copy(obj.scale)
    next.renderOrder = obj.renderOrder
    next.visible = obj.visible
    obj.children.forEach((child) => next.add(cloneObject(child)))
    return next
  }

  return cloneObject(group) as THREE.Group
}

const buildChildrenTemplateGroup = (engine: any, children: BnBNode[], highlightNodeId: string | null): THREE.Group => {
  const template = new THREE.Group()

  const childMeshPairs: { node: BnBNode; mesh: THREE.Mesh }[] = []
  children.forEach((child) => {
    try {
      const mesh = createMeshFromInequalities(child.problem.inequalities, {
        equalityPlaneFallback: false,
        errorFallbackInvisible: true,
      })
      childMeshPairs.push({ node: child, mesh })
    } catch {
      // ignore single-child failures and continue with fallback path
    }
  })

  const unionMesh = unionMeshes(childMeshPairs.map((p) => p.mesh))

  if (unionMesh) {
    template.add(unionMesh)

    const pos = (unionMesh.geometry as THREE.BufferGeometry).attributes.position
    const hasVerts = pos && typeof pos.count === 'number' && pos.count > 0
    if (hasVerts) {
      const edgesGeometry = new THREE.EdgesGeometry(unionMesh.geometry, 1)
      const wireframe = new THREE.LineSegments(
        edgesGeometry,
        new THREE.LineBasicMaterial({ color: 0x5dade2, transparent: true, opacity: 0.85, linewidth: 1, depthTest: false }),
      )
      wireframe.renderOrder = 50
      template.add(wireframe)
    }

    let highlighted = childMeshPairs.find((p) => p.node.id === highlightNodeId) || null
    if (!highlighted && childMeshPairs.length > 0) highlighted = childMeshPairs[0]

    if (highlighted) {
      try {
        const g = highlighted.mesh.geometry as THREE.BufferGeometry
        const p = g.attributes.position
        const hasChildVerts = g && p && typeof p.count === 'number' && p.count > 0
        if (hasChildVerts) {
          const childEdges = new THREE.EdgesGeometry(highlighted.mesh.geometry, 1)
          const childWire = new THREE.LineSegments(
            childEdges,
            new THREE.LineBasicMaterial({
              color: 0x22d3ee,
              transparent: true,
              opacity: 0.45,
              depthTest: true,
              depthWrite: false,
            }),
          )
          childWire.renderOrder = 80
          template.add(childWire)
        }
      } catch {
        // ignore highlight edge failures
      }
    }
  } else {
    children.forEach((child) => {
      const childGroup = engine.polyManager.buildGroupFromInequalities(child.problem.inequalities, { equalityPlaneFallback: false })
      engine.scene.remove(childGroup)
      template.add(childGroup)
    })
  }

  childMeshPairs.forEach(({ mesh: m }) => {
    m.geometry?.dispose?.()
    disposeMaterial(m.material as THREE.Material | THREE.Material[] | undefined)
  })

  return template
}

interface MeshCacheEntry {
  signature: string
  template: THREE.Group
}

export class BnBMeshCache {
  private entries = new Map<string, MeshCacheEntry>()

  getOrBuild(nodeId: string, signature: string, builder: () => THREE.Group): THREE.Group {
    const cached = this.entries.get(nodeId)
    if (cached && cached.signature === signature) {
      return cloneGroupDeep(cached.template)
    }

    if (cached) {
      disposeObject3DDeep(cached.template)
      this.entries.delete(nodeId)
    }

    const template = builder()
    this.entries.set(nodeId, { signature, template })
    return cloneGroupDeep(template)
  }

  evictNode(nodeId: string): void {
    const cached = this.entries.get(nodeId)
    if (!cached) return
    disposeObject3DDeep(cached.template)
    this.entries.delete(nodeId)
  }

  clearAll(): void {
    for (const cached of this.entries.values()) {
      disposeObject3DDeep(cached.template)
    }
    this.entries.clear()
  }
}

const bnbMeshCache = new BnBMeshCache()

export function getBnBMeshCache(): BnBMeshCache {
  return bnbMeshCache
}

const makeNodeSignature = (node: BnBNode, children: BnBNode[], highlightNodeId: string | null): string => {
  const childrenSignature = children
    .map((child) => `${child.id}:${child.status}:${child.lp?.optimalValue ?? 'na'}:${child.bounds.length}`)
    .join('|')
  return `${node.id}::${childrenSignature}::highlight=${highlightNodeId ?? 'auto'}`
}

export function assembleBnBScene(
  engine: any,
  node: BnBNode,
  children: BnBNode[],
  highlightNodeId: string | null,
): void {
  engine.currentProblem = node.problem

  if (engine.polyhedronGroup) {
    engine.polyManager.disposeGroup(engine.polyhedronGroup)
    engine.polyhedronGroup = null
  }

  if (children.length > 0) {
    const signature = makeNodeSignature(node, children, highlightNodeId)
    engine.polyhedronGroup = bnbMeshCache.getOrBuild(node.id, signature, () =>
      buildChildrenTemplateGroup(engine, children, highlightNodeId),
    )
    engine.scene.add(engine.polyhedronGroup)

    if (engine.currentProblem) {
      engine.polyManager.addIntegerPointsAndAxes(engine.polyhedronGroup, engine.currentProblem, {
        showHull: false,
        overrideLPSolution: [],
        hasFractionalSolution: undefined,
      })
    }

    children.forEach((child) => {
      if (child.lp && Array.isArray(child.lp.solution) && child.lp.solution.length === 3) {
        engine.polyManager.addLPMarker(engine.polyhedronGroup, child.lp.solution, !!child.lp.hasFractionalSolution)
      }
    })
    return
  }

  bnbMeshCache.evictNode(node.id)
  engine.polyhedronGroup = engine.polyManager.buildGroupFromInequalities(engine.currentProblem.inequalities, {
    equalityPlaneFallback: false,
  })

  let override: number[] | undefined
  let hasFrac: boolean | undefined
  try {
    const st = engine.bnbManager?.getState()
    const currentNode = st?.nodes.get(node.id)
    if (currentNode && Array.isArray(currentNode.lp?.solution) && currentNode.lp.solution.length === 3) {
      override = currentNode.lp.solution
      hasFrac = !!currentNode.lp.hasFractionalSolution
    }
  } catch {
    // ignore store lookup errors
  }

  engine.polyManager.addIntegerPointsAndAxes(engine.polyhedronGroup, engine.currentProblem, {
    showHull: false,
    overrideLPSolution: override,
    hasFractionalSolution: hasFrac,
  })
}
